/** Small, accessible charts for the operator console. Values always arrive from an API caller. */
import { h, cx, type ReactNode } from "./h.ts";

export interface BarDatum {
  label: string;
  value: number;
  display?: string;
  note?: string;
}

export function StatGrid({ items, columns }: { items: Array<{ label: string; value: ReactNode; note?: string; tone?: "plain" | "attention" | "good" }>; columns?: 2 | 3 | 4 }): ReactNode {
  return h("div", { className: cx("oc-stat-grid", columns && `cols-${columns}`) }, ...items.map((item) =>
    h("div", { key: item.label, className: cx("oc-stat", item.tone && `is-${item.tone}`) },
      h("div", { className: "label" }, item.label),
      h("div", { className: "value oc-tnum" }, item.value),
      item.note ? h("div", { className: "note" }, item.note) : null,
    )));
}

export function BarChart({ title, items, max }: { title: string; items: BarDatum[]; max?: number }): ReactNode {
  const ceiling = Math.max(max ?? 0, ...items.map((item) => item.value), 1);
  return h("figure", { className: "oc-bar-chart", "aria-label": title },
    h("figcaption", null, title),
    h("div", { className: "bars" }, ...items.map((item) => {
      const width = Math.max(0, Math.min(100, (item.value / ceiling) * 100));
      return h("div", { key: item.label, className: "bar-row" },
        h("div", { className: "bar-label" }, item.label),
        h("div", { className: "bar-track", role: "img", "aria-label": `${item.label}: ${item.display ?? item.value}` },
          h("span", { className: cx("bar-fill", item.value === 0 && "is-zero"), style: { width: `${width}%` } })),
        h("div", { className: "bar-value oc-tnum" }, item.display ?? String(item.value)),
        item.note ? h("div", { className: "bar-note" }, item.note) : null,
      );
    })),
  );
}

export function ReadinessMeter({ complete, total, label = "Evidence readiness" }: { complete: number; total: number; label?: string }): ReactNode {
  const safeTotal = Math.max(total, 1);
  const safeComplete = Math.max(0, Math.min(complete, safeTotal));
  const percent = Math.round((safeComplete / safeTotal) * 100);
  return h("div", { className: "oc-readiness" },
    h("div", { className: "readiness-head" },
      h("span", null, label),
      h("strong", { className: "oc-tnum" }, `${safeComplete}/${total} · ${percent}%`)),
    h("progress", { max: safeTotal, value: safeComplete, "aria-label": `${label}: ${safeComplete} of ${total}` }, String(percent)),
  );
}
